import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Home, Loader2, AlertTriangle, ArrowLeft } from 'lucide-react';
import api from '@/lib/axios';
import MarketplaceFooter from '@/components/marketplace/MarketplaceFooter';
import { TipoBadge } from '@/pages/NewsCenterPage';

export default function NewsCategoryPage() {
  const { slug } = useParams();
  const [items, setItems] = useState([]);
  const [nome, setNome] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.get('/marketplace/news/categories').then((r) => {
      const c = (r.data.items || []).find((x) => x.slug === slug);
      setNome(c?.nome || slug.replace(/-/g, ' '));
    }).catch(() => setNome(slug.replace(/-/g, ' ')));
  }, [slug]);

  const load = useCallback(async () => {
    setLoading(true); setError(false);
    try {
      const r = await api.get('/marketplace/news/articles', { params: { categoria: slug, page, limit: 9 } });
      setItems(r.data.items || []); setTotal(r.data.total || 0);
    } catch (e) { setError(true); } finally { setLoading(false); }
  }, [slug, page]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => { document.title = `${nome} | Centro de Informação TVDE`; }, [nome]);
  const totalPages = Math.max(1, Math.ceil(total / 9));

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-4">
          <Link to="/" className="inline-flex items-center gap-2 text-sm font-bold text-slate-700 hover:text-emerald-700"><Home className="h-4 w-4" /> Marketplace TVDE</Link>
          <Link to="/noticias-tvde" className="inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-700"><ArrowLeft className="h-4 w-4" /> Centro de Informação</Link>
        </div>
      </header>
      <div className="mx-auto max-w-6xl px-4 py-8">
        <p className="text-xs font-semibold uppercase tracking-wide text-emerald-600">Tema</p>
        <h1 className="text-3xl font-extrabold capitalize text-slate-900">{nome}</h1>
        <div className="mt-6">
          {loading ? (
            <div className="flex items-center justify-center rounded-2xl border border-slate-200 bg-white py-16 text-slate-500" data-testid="cat-loading"><Loader2 className="mr-2 h-5 w-5 animate-spin" /> A carregar…</div>
          ) : error ? (
            <div className="rounded-2xl border border-amber-200 bg-amber-50 p-10 text-center" data-testid="cat-error"><AlertTriangle className="mx-auto h-8 w-8 text-amber-500" /><p className="mt-3 font-bold text-amber-900">Falha temporária de ligação.</p><button onClick={load} className="mt-4 rounded-full bg-amber-500 px-5 py-2 text-sm font-bold text-white">Tentar novamente</button></div>
          ) : items.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center" data-testid="cat-empty"><p className="font-semibold text-slate-700">Ainda não há conteúdos publicados neste tema.</p></div>
          ) : (
            <>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="cat-grid">
                {items.map((a) => (
                  <Link key={a.slug} to={`/noticias-tvde/artigo/${a.slug}`} className="flex flex-col rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition hover:border-emerald-300">
                    <TipoBadge tipo={a.tipo} />
                    <h3 className="mt-2 font-extrabold text-slate-900">{a.titulo}</h3>
                    {a.resumo && <p className="mt-1 line-clamp-3 text-sm text-slate-600">{a.resumo}</p>}
                  </Link>
                ))}
              </div>
              {totalPages > 1 && (
                <div className="mt-8 flex items-center justify-center gap-2">
                  <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm font-semibold disabled:opacity-40">Anterior</button>
                  <span className="text-sm text-slate-600">Página {page} de {totalPages}</span>
                  <button disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm font-semibold disabled:opacity-40">Seguinte</button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
      <MarketplaceFooter />
    </div>
  );
}
